import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { Router } from '@angular/router';
import { Observable, concat, of, from, Subject, Subscription } from 'rxjs';
import { map, switchMap, concatMap, debounceTime } from 'rxjs/operators';

import { LazyLoadEvent, MenuItem } from 'primeng/api';
import { Table } from 'primeng/table';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { CliListEntry } from 'src/app/shared/cli-list-entry';
import { Client } from 'src/app/shared/client';
import { ClientApiService } from 'src/app/services/client-api.service';

/** Column descriptor for the clients PrimeNG table. */
export class column {
  field = '';
  header = '';
  nonhide: boolean;
  show: boolean;
  filter: boolean;
};

@Component({
  selector: 'app-clients',
  templateUrl: './clients.component.html',
  styleUrls: [ './clients.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Client (organisation / counterparty) list page.
 *
 * Driven by three Subjects:
 * - `clients$` — full reload with `debounceTime(300)` → `switchMap(getCliListSliceFilter)`.
 *   Pushes the entire result set and slices the first page into `clientslist`.
 * - `cliPage$` — lazy page slice emitted by PrimeNG's `onLazyLoad`; fetches just the
 *   requested window from the API.
 * - `update$` — async row enrichment pipeline; for each row it calls
 *   `getCustomerBusinessAreaByID` (sets `CUSTOMERBUISNESSAREA`) and `getCustomerCategoryByID`
 *   (sets `CUSTOMERCATEGORY`), then adds a ">> Без названия <<" placeholder for rows
 *   without a nickname.
 *
 * Column visibility is persisted to `localStorage` under `storeNameShowRowOptions`
 * (`'clients-columns-1'`) on `ngOnDestroy`.
 */
export class ClientsComponent implements OnInit, OnDestroy {

  private globalSettinsData: GlobalSettingsData;

/*
                        '([CLIID] '+
                        ',[CLISUBSOF] '+
                        ',[CLICATEGORY] '+
                        ',[CASTCATID] ' +
                        ',[CLILOGOID] ' +
                        ',[CLISTAMPID] ' +
                        ',[CURID] ' +

                        ',[CLIENTSNIKNAME] '+
                        ',[CLIENTSNAME] '+
                        ',[CLIENTCOUNTRY] '+
                        ',[CLIENTCITY] '+
                        ',[CLIENTADDR1] '+
                        ',[CLIENTADR2] '+
                        ',[CLIENTZIP] '+
                        ',[CLIENTBANK] '+
                        ',[CLIENTACCOUNT] '+
                        ',[CLIENTBANK2ACC] ' +
                        ',[CLIENTREGCODE] '+
                        ',[CLIENTVATREGCODE] '+
                        ',[CLIENTBANKREGCODE] '+
                        ',[CLIENTBANKCODE] '+
                        ',[CLEINTBANKCITY] '+
                        ',[CLIENTBANKADDR1] '+
                        ',[CLENTBANKADDR2] '+
                        ',[CLIENTBANKZIP] '+
                        ',[CLIENTBANKPHONE] '+
                        ',[CLIENTNOTE1] '+
                        ',[CLIENTNOTE2] '+
                        ',[CLIENTVATREGNUM] '+
                        ',[CLIDOCINDEX] ' +
 */

/*
 * TODO: 4) Expandable row (????)
 * 
 */

  clientslist: CliListEntry[];
  datasource: CliListEntry[];
  client: Client;

  clientSubsQuery: Subscription;
  cliPageQuery: Subscription;
  updNameQuery: Subscription;

  clientslistentry: CliListEntry;

  totalRecords: number;

  rows: number = 25;

  loading: boolean = true;

  private storeNameFilterOptions: string = 'clients-filters-1';

  cfs: any [string] = [];

  items: MenuItem[];

  @ViewChild('dt') table: Table;

  search$: Observable<string>;
  private clients$ = new Subject<any>();
  private cliPage$ = new Subject<{ from: number, to: number, option: any }>();
  private update$ = new Subject<CliListEntry[]>();

  private storeNameShowRowOptions: string = 'clients-columns-1';

  showPersRowsOptions = [
    { name: 'Реквизиты', value: 1 },
    { name: 'Банк (Счета)', value: 2 },
    { name: 'Банк (Адрес)', value: 16 },
    { name: 'Адрес', value: 4 },
    { name: 'Примечание', value: 8 },
  ];

  showRowOptions: number[] = [];

  /** `true` when the client list is empty. */
  public get noItems(): boolean { return ((this.clientslist || []).length < 1); }

  /** `true` when the "Note" column group is selected for display. */
  public get seeNote(): boolean { return this.showRowOptions.includes(8); }
  /** `true` when the "Codes" (registration/VAT/bank) column group is selected for display. */
  public get seeCodes(): boolean { return this.showRowOptions.includes(1); }
  /** `true` when the "Bank accounts" column group is selected for display. */
  public get seeBanks(): boolean { return this.showRowOptions.includes(2); }
  /** `true` when the "Bank address" column group is selected for display. */
  public get seeBAddr(): boolean { return this.showRowOptions.includes(16); }
  /** `true` when the "Address" column group is selected for display. */
  public get seeAddress(): boolean { return this.showRowOptions.includes(4); }

  constructor(
    private cliApi: ClientApiService,
    private router: Router,
    private titleService: Title
    ) {
        this.items = [
          { label: 'Открыть/Редактировать', icon: 'pi pi-fw pi-user-edit', command: () => this.viewCliListEntry(this.clientslistentry) },
          { label: 'Удалить', icon: 'pi pi-fw pi-times', command: () => this.deleteCliListEntry(this.clientslistentry) }
        ];

    }

  /** Persists column-visibility options to localStorage and unsubscribes all queries. */
  ngOnDestroy(): void {
    console.log('*** ClientsComponent in ngOnDestroy');

    localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));

    if (this.clientSubsQuery) { this.clientSubsQuery.unsubscribe(); }

    if (this.cliPageQuery) { this.cliPageQuery.unsubscribe(); }

    if (this.updNameQuery) { this.updNameQuery.unsubscribe(); }
  }

  /** Wires up the three Subjects, restores saved column-visibility, and triggers the initial load. */
  ngOnInit(): void {
    console.log('*** ClientsComponent in ngOnInit');

    this.titleService.setTitle('Контрагенты');

    this.updNameQuery =
      this.update$.pipe(
        switchMap(list => from(list).pipe(
          concatMap(cl => this.cliApi.getCustomerBusinessAreaByID(cl.CLIID).pipe(
            concatMap(data => {
              cl.CUSTOMERBUISNESSAREA = data.map(a => a['CUSTOMERTYPE'])[0];

              return of(cl);
            })
          )),
          concatMap(cl => this.cliApi.getCustomerCategoryByID(cl.CASTCATID).pipe(
            concatMap(cat => {
              if (cat.length > 0) {
                cl.CUSTOMERCATEGORY = cat[0].CASTCATNAME;
              }
              return of(cl);
            })
          )),
          concatMap(cl => {
            if (!(cl && cl.CLIENTSNIKNAME && (cl.CLIENTSNIKNAME.trim().length > 0))) {
              cl.CLIENTSNIKNAME = '>> Без названия ' + cl.CLIID + ' <<';
            }
            return of(cl);
          }),

        )))
        .subscribe();

    this.cliPageQuery =

      this.cliPage$.pipe(
        switchMap((opt: any) => {

          this.loading = true;
          return this.cliApi.getCliListSliceFilter(opt.from, opt.to, opt.option)
            .pipe(
              map(data => {

              this.clientslist = data;
              this.update$.next(this.clientslist);

              this.loading = false;

            }));
        })).subscribe();

    this.clientSubsQuery =

      this.clients$.pipe(
        debounceTime(300),
        switchMap(options => {

          this.loading = true;
          return this.cliApi.getCliListSliceFilter(0, 10000000, options)
            .pipe(
              map(data => {
              this.totalRecords = data.length;
              this.clientslist = data.slice(0, this.table ? this.table.rows : this.rows);

              this.update$.next(this.clientslist);

              this.loading = false;
            })
          );
        })).subscribe();

    let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
    if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }

    let getoptions: any = {};

    if (this.table) {
      //console.log('*** ProductListComponent in createProductSubscription: before spread prodtable filter' + JSON.stringify( getoptions));
      getoptions = {
        sp: {
          sf: this.table.sortField,
          so: this.table.sortOrder
        },
        fs: this.table.filters
      }
      //console.log('*** ProductListComponent in createProductSubscription: before after spread prodtable filter' + JSON.stringify(getoptions));
    }

    this.clients$.next(getoptions);
  }

  /** PrimeNG `onLazyLoad` handler — pushes the requested page window to `cliPage$`. */
  loadClientList(event: LazyLoadEvent) {
    //in a real application, make a remote request to load data using state metadata from event
    //event.first = First row offset
    //event.rows = Number of rows per page
    //event.sortField = Field name to sort with
    //event.sortOrder = Sort order as number, 1 for asc and -1 for dec
    //filters: FilterMetadata object having field as key and filter value, filter matchMode as value

    console.log('*** ClientsComponent Lazy Load initiated');
    console.log(event.filters);

    this.cliPage$.next({
      from: event.first, to: (event.first + event.rows), option: {
        sp: {
          sf: event.sortField,
          so: event.sortOrder
        },
        fs: { ...event.filters }
      }
    });
  }

  /** Triggers a full reload via `clients$` whenever a column filter value changes. */
  filterProdTable(event) {
    console.log('*** ProductListComponent filterProdTable: ' + JSON.stringify(event.filters));
    this.clients$.next({
      sp: {
        sf: this.table.sortField,
        so: this.table.sortOrder
      },
      fs: this.table.filters
    });
  }

  /** Stores the last selected row entry (diagnostic log). */
  onRowSelect(event) {
    console.log('*** ClientsComponent onRowSelect: ' + JSON.stringify(event));
  }

  /** No-op handler for row unselection. */
  onRowUnselect(event) {
  }

  /** Navigates to the client editor for the selected list entry. */
  viewCliListEntry(cle: CliListEntry) {
    console.log('*** ClientsComponent viewCliListEntry: ' + JSON.stringify(cle));
    this.router.navigate(['/edit-client', cle.CLIID]);
  }

  /** Navigates to the client editor in new-client mode (id 0). */
  onCreateNewClient(event) {
    console.log('*** ClientsComponent onCreateNewClient: ' + JSON.stringify(event))
    this.router.navigate(['/edit-client', 0]);
  }

  /** Delete stub — not yet implemented. */
  deleteCliListEntry(cle: CliListEntry) {
    //this.products = this.products.filter((p) => p.id !== product.id);
    //this.messageService.add({ severity: 'info', summary: 'Product Deleted', detail: product.name });
    //this.selectedProduct = null;
  }

  /** PrimeNG table state-save callback (currently a no-op). */
  onStateSave(state) {
    //    console.log('*** ClientsComponent onStateSave: ' + JSON.stringify(state));
  }

  /** Restores filter values and row count from a previously saved table state. */
  onStateRestore(state) {
    //console.log('*** ClientsComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cfs[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }

    if (state.rows) {
      this.rows = state.rows;
    }
  }

  /** Clears all table filters, resets state, and triggers a full reload via `clients$`. */
  clearOrResetTableFilter(event) {
    console.log('*** ClientsComponent clearOrResetTableFilter: ' + JSON.stringify(event));

    this.table.clearState();
    this.table.clear();
    this.table.reset();

    this.table.filters = {};

    for (let f in this.cfs) {
      this.cfs[f] = "";
    }

    this.clients$.next({});
}

  /** Forces a full reload while preserving the current sort and filter state. */
  onReloadAllData(event) {
    console.log('*** ClientsComponent onReloadAllData: ' + JSON.stringify(event));

    let getoptions: any = {};

    if (this.table) {
      getoptions = {
        sp: {
          sf: this.table.sortField,
          so: this.table.sortOrder
        },
        fs: this.table.filters
      }
    }
    this.clients$.next(getoptions);
  }

  /** Handles a change in the row-display options multiselect (currently a no-op). */
  onRowsOptionChange(event) {
    //  console.log('*** ClientsComponent in onRowsOptionChange: ' + JSON.stringify(event));
  }
}
